import { MapPin, Sun, Moon, Clock, Phone, Venus, Stethoscope } from "lucide-react";
import { SITE } from "@/lib/site";

export default function Availability() {
  return (
    <section
      id="availability"
      data-testid="availability-section"
      className="bg-white border-y border-gray-100"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-10 lg:px-12 py-16 md:py-24">
        <div className="grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-4">
            <span className="text-xs font-bold uppercase tracking-[0.22em] text-[#0369A1]">
              Radiologist Availability
            </span>
            <h2 className="font-display mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#0B2545]">
              Our consultant radiologists.
            </h2>
            <p className="mt-4 text-[#64748B] leading-relaxed">
              Walk in or pre-book your scan during these hours for live reporting and a
              real-time consultation with the radiologist.
            </p>

            <div className="mt-6 rounded-2xl border border-[#FBCFE8] bg-[#FDF2F8] p-5">
              <div className="inline-flex items-center gap-2 text-[#BE185D] font-bold text-sm">
                <Venus className="w-4 h-4" /> Female Consultant Radiologist
              </div>
              <p className="mt-2 text-sm text-[#831843] leading-relaxed">
                <strong>Dr. Hithishini H</strong> is available at our Hosa Road branch — for
                the comfort and privacy of our women patients during pregnancy, pelvic &
                breast scans.
              </p>
            </div>

            <div className="mt-6 inline-flex items-center gap-2 text-sm text-[#64748B]">
              <Clock className="w-4 h-4 text-[#0369A1]" /> Mon – Sat · All times in IST (AM/PM)
            </div>
          </div>

          <div className="lg:col-span-8 grid sm:grid-cols-2 gap-5">
            {SITE.branches.map((b, i) => (
              <div
                key={b.name}
                data-testid={`availability-card-${i}`}
                className="bg-[#F0F9FF] border border-[#BAE6FD] rounded-2xl p-6 md:p-7 flex flex-col"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-[#0369A1]">
                    <MapPin className="w-4 h-4" />
                    <span className="text-xs font-bold tracking-[0.18em] uppercase">
                      {b.label}
                    </span>
                  </div>
                  {b.isMain && (
                    <span className="text-[10px] font-bold tracking-[0.16em] uppercase bg-[#0369A1] text-white px-2.5 py-1 rounded-full">
                      Main
                    </span>
                  )}
                </div>

                <h3 className="font-display mt-2 text-2xl font-bold text-[#0B2545]">
                  {b.name}
                </h3>

                {/* Doctor profile */}
                <div className="mt-4 flex items-center gap-3 bg-white rounded-xl p-3 border border-[#E0F2FE]">
                  {b.doctor.photo ? (
                    <img
                      src={b.doctor.photo}
                      alt={b.doctor.name}
                      loading="lazy"
                      className="w-14 h-14 rounded-xl object-cover"
                    />
                  ) : (
                    <span className="grid place-items-center w-14 h-14 rounded-xl bg-[#0369A1] text-white font-display font-bold text-lg shrink-0">
                      {b.doctor.name
                        .replace(/^Dr\.?\s*/i, "")
                        .split(" ")
                        .map((w) => w[0])
                        .slice(0, 2)
                        .join("")}
                    </span>
                  )}
                  <div>
                    <div className="flex items-center gap-1.5">
                      <span className="font-display font-bold text-[#0B2545]">
                        {b.doctor.name}
                      </span>
                      {b.doctor.isFemale && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold text-[#BE185D] bg-[#FDF2F8] px-1.5 py-0.5 rounded-full">
                          <Venus className="w-2.5 h-2.5" /> Female
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-[#64748B]">{b.doctor.qualifications}</div>
                    <div className="inline-flex items-center gap-1 text-xs font-semibold text-[#0369A1] mt-0.5">
                      <Stethoscope className="w-3 h-3" /> {b.doctor.title}
                    </div>
                  </div>
                </div>

                <div className="mt-4 space-y-3">
                  <div className="flex items-center justify-between bg-white rounded-xl px-4 py-3">
                    <span className="inline-flex items-center gap-2 text-sm font-semibold text-[#0B2545]">
                      <Sun className="w-4 h-4 text-[#F59E0B]" /> Morning
                    </span>
                    <span className="font-display font-bold text-[#0369A1]">{b.morning}</span>
                  </div>
                  <div className="flex items-center justify-between bg-white rounded-xl px-4 py-3">
                    <span className="inline-flex items-center gap-2 text-sm font-semibold text-[#0B2545]">
                      <Moon className="w-4 h-4 text-[#6366F1]" /> Evening
                    </span>
                    <span className="font-display font-bold text-[#0369A1]">{b.evening}</span>
                  </div>
                </div>

                <p className="mt-4 text-xs text-[#475569] leading-relaxed">{b.address}</p>

                <a
                  href={`tel:${b.phoneRaw}`}
                  data-testid={`availability-call-${i}`}
                  className="mt-4 inline-flex items-center justify-center gap-2 h-10 rounded-full bg-[#0369A1] hover:bg-[#0284C7] text-white text-sm font-semibold transition-colors"
                >
                  <Phone className="w-4 h-4" /> {b.phones[0]}
                </a>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
